import { getLangDir, getLocalMonthYear, incrementToFirstOfMonth, isBetweenDays, isFirstDayOfMonth, isLastDayOfMonth, isSameDay, isSameMonth, stepDay, } from '@tesla/design-system';
import cx from 'classnames';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useCalendarGrid } from '../index.js';
import { Day, DaysOfWeek, MonthHeader } from './index.js';
import { dateToIsoDateString, getDatePickerDate } from './utils/index.js';
export const DateRangePicker = ({ ariaLabelDayPicker = 'Use arrow keys to increment the date', ariaLabelMonthPicker = 'Use left or right arrow keys to increment the month', className, dateType, density, disabled, end: selectedEnd, error, firstDayOfWeek = 0, layout, locale = 'en-US', onDateChange, start: selectedStart, variant, ...rest }) => {
    // Create a fallback to still render the correct month and year,
    // even if no date is passed in at first (allows for no default selected date)
    const startDate = selectedStart ? getDatePickerDate(selectedStart, dateType) : null;
    const endDate = selectedEnd ? getDatePickerDate(selectedEnd, dateType) : null;
    const [month, setMonth] = useState(startDate || new Date());
    const [hoveredDate, setHoveredDate] = useState(null);
    const [focusedDate, setFocusedDate] = useState(null);
    // Set the month for the second calendar grid
    const month2 = incrementToFirstOfMonth(month, 1);
    // Set the next and previous months
    const prevMonth = incrementToFirstOfMonth(month, -1);
    const nextMonth = incrementToFirstOfMonth(month, 1);
    useLayoutEffect(() => {
        if (focusedDate) {
            setMonth((prevState) => {
                const monthPlus = new Date(prevState);
                // Assume monthPlus is already zero'd out by the initial render
                monthPlus.setMonth(prevState.getMonth() + 1);
                // If focused date is within the two visible months
                // do nothing.
                if (isSameMonth(focusedDate, prevState) || isSameMonth(focusedDate, monthPlus)) {
                    return prevState;
                }
                let updatedMonth = new Date(prevState);
                updatedMonth.setDate(1);
                if (focusedDate < prevState) {
                    updatedMonth = incrementToFirstOfMonth(prevState, 1);
                }
                else {
                    updatedMonth = incrementToFirstOfMonth(prevState, -1);
                }
                return updatedMonth;
            });
        }
    }, [focusedDate]);
    // Create a list of days with some data about each one, only when the month changes
    const days = useCalendarGrid(month, firstDayOfWeek);
    const days2 = useCalendarGrid(month2, firstDayOfWeek);
    // Update one or both dates depending on the user's selection
    const handleDateChange = (day) => {
        let start = null;
        let end = null;
        if (
        // If no dates selected yet: Select start date
        (!startDate && !endDate) ||
            // If start date selected and day is earlier: Select start date
            (startDate && day < startDate) ||
            // If end is selected, and day is later, Select start date
            (endDate && day > endDate)) {
            start = day;
        }
        // If start date is selected and user clicked on it again
        if (startDate && !endDate && isSameDay(day, startDate)) {
            start = day;
            end = day;
        }
        // If start date selected and day is later: Select end date
        if (startDate && !endDate && day > startDate) {
            start = startDate;
            end = day;
        }
        return { end, start };
    };
    const handleDayKeyDown = (e, day) => {
        const dir = getLangDir(e.target);
        if ((dir === 'ltr' && e.key === 'ArrowRight') || (dir === 'rtl' && e.key === 'ArrowLeft')) {
            e.preventDefault();
            setFocusedDate(stepDay(day, 1));
        }
        if ((dir === 'ltr' && e.key === 'ArrowLeft') || (dir === 'rtl' && e.key === 'ArrowRight')) {
            e.preventDefault();
            setFocusedDate(stepDay(day, -1));
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedDate(stepDay(day, 7));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedDate(stepDay(day, -7));
        }
    };
    const handleDayHover = (day) => {
        setHoveredDate(endDate ? null : day);
    };
    const handleDaySelect = (e, day) => {
        setHoveredDate(null);
        const { end, start } = handleDateChange(day);
        if (dateType === 'string') {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange({
                end: dateToIsoDateString(end),
                start: dateToIsoDateString(start),
            });
        }
        else {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange({
                end,
                start,
            });
        }
        if (end && start && e.currentTarget instanceof HTMLElement)
            e.currentTarget.blur();
    };
    // Get a bunch of boolean logic (TODO: Move grids into their own component?)
    const getDayRangeProps = useCallback((day) => {
        // Reference points
        const first = isFirstDayOfMonth(day);
        const last = isLastDayOfMonth(day);
        const start = !!startDate && isSameDay(day, startDate);
        const end = !!endDate && isSameDay(day, endDate);
        const hovered = !start &&
            !!startDate &&
            day > startDate &&
            !!(hoveredDate || focusedDate) &&
            !endDate &&
            isSameDay(day, (hoveredDate || focusedDate));
        const betweenStartAndEnd = !!startDate && !!endDate ? isBetweenDays(day, startDate, endDate) : false;
        const betweenStartAndHovered = !!startDate && !!hoveredDate ? isBetweenDays(day, startDate, hoveredDate) : false;
        // States
        const selected = start || end || (!!startDate && !!endDate && betweenStartAndEnd);
        const highlighted = hovered || (!!startDate && !!hoveredDate && betweenStartAndHovered);
        return {
            between: betweenStartAndHovered || betweenStartAndEnd,
            end,
            first,
            highlighted,
            last,
            selected,
            start,
        };
    }, [endDate, startDate, hoveredDate, focusedDate]);
    return (React.createElement("div", { className: cx('tds-date-picker', 'tds-date-picker--range', { [`tds-density--${density}`]: density }, { [`tds-date-picker--${variant}`]: variant }, { [`tds-date-picker--${layout}`]: layout }, { 'tds-date-picker--hovering': startDate && !!hoveredDate }, { 'tds-date-picker--selected': startDate && !!endDate }, className), ...rest },
        React.createElement(MonthHeader, { ariaLabelMonthPicker: ariaLabelMonthPicker, date: month, locale: locale, months: layout === 'vertical' ? 1 : 2, onNextClick: () => {
                setFocusedDate(null);
                setMonth(nextMonth);
            }, onPrevClick: () => {
                setFocusedDate(null);
                setMonth(prevMonth);
            } }),
        React.createElement("div", { className: "tds-date-picker-calendar" },
            React.createElement(DaysOfWeek, { firstDayOfWeek: firstDayOfWeek, locale: locale }),
            React.createElement("div", { "aria-label": ariaLabelDayPicker, className: "tds-date-picker-days-grid", onMouseLeave: () => setHoveredDate(null) }, days.map((day) => (React.createElement(Day, { date: day, error: error, focused: isSameDay(day, focusedDate), isDisabled: disabled, key: `${day.getDate()}_${day.getMonth()}`, month: month, onDateSelect: handleDaySelect, onKeyDown: (e) => handleDayKeyDown(e, new Date(day)), onMouseEnter: () => handleDayHover(day), ...getDayRangeProps(day) }))))),
        layout === 'vertical' && (React.createElement("div", { className: "tds-date-picker-month" },
            React.createElement("label", null, getLocalMonthYear(nextMonth, locale)))),
        React.createElement("div", { className: "tds-date-picker-calendar" },
            layout !== 'vertical' && React.createElement(DaysOfWeek, { firstDayOfWeek: firstDayOfWeek, locale: locale }),
            React.createElement("div", { "aria-label": ariaLabelDayPicker, className: "tds-date-picker-days-grid", onMouseLeave: () => setHoveredDate(null) }, days2.map((day) => (React.createElement(Day, { date: day, error: error, focused: isSameDay(day, focusedDate), isDisabled: disabled, key: `${day.getDate()}_${day.getMonth()}`, month: month2, onDateSelect: handleDaySelect, onKeyDown: (e) => handleDayKeyDown(e, new Date(day)), onMouseEnter: () => handleDayHover(day), ...getDayRangeProps(day) })))))));
};
